import React, { useEffect, useState } from 'react';
import { Form, FormInstance } from 'antd';
import {
  DatePicker,
  Input,
  Modal,
  Select,
  Spinner,
} from 'UI';
import {
  Client,
  SeedBatch,
  Variety,
} from 'constants/interface';
import { useDispatch, useSelector } from 'hooks/store';
import {
  cleanVarietiesAction,
  getVarietyListAction,
  isLoadingVarietiesSelector,
  varietiesEntitiesSelector,
  varietiesIdsSelector,
} from 'state/slices/products';
import {
  CostCenterEnum,
  InventoryCostCenterMapping,
  InventoryOutputTypeEnum,
  InventoryOutputReasonMapping,
} from 'constants/inventory';
import {
  clientSeedBatchesEntitiesSelector,
  isLoadingClientSeedBatchesSelector,
  clientSeedBatchesIdsSelector,
  getClientSeedBatchesListAction,
  cleanClientSeedBatchesAction,
  clientsEntitiesSelector,
  clientsIdsSelector,
  isLoadingClientsSelector,
  cleanClientsAction,
  getClientsListAction,
} from 'state/slices/clients';
import dayjs, { Dayjs } from 'dayjs';
import { PlusOutlined } from '@ant-design/icons';

import {
  FormContainer,
  BaseFormItem,
  InlineButton,
  RemoteSelectContent,
  RemoteSelectSpinnerContainer,
  QuantityFormItem,
  NotesInline,
  FormItemRow,
  AddEntryItemButton,
  EntryItemFormRow,
  EntryItemInput,
  RemoveEntryItemButton,
  RemoveEntryItemIcon,
} from './style';

interface EntryItemFormData {
  variety: string
  batchId: string
  quantity: string
}

export interface EntryOutputFormData {
  entryDate: Dayjs
  client: string
  costCenter: CostCenterEnum
  outputType: InventoryOutputTypeEnum
  notes: string
  items: EntryItemFormData[]
}

interface EntryOutputModalProps {
  formHandler: FormInstance
  onSubmit(data: unknown): void
  onCancel(): void
  isLoading: boolean
  isVisible: boolean
}

const EntryOutputModal = ({
  formHandler,
  onSubmit,
  onCancel,
  isLoading,
  isVisible,
}: EntryOutputModalProps): JSX.Element => {
  const dispatch = useDispatch();

  const varietySearchEntities = useSelector(varietiesEntitiesSelector);
  const varietySearchIds = useSelector(varietiesIdsSelector);
  const isLoadingVarieties = useSelector(isLoadingVarietiesSelector);
  const varietySearchResult = varietySearchIds
    .map((varietyId) => varietySearchEntities[varietyId])
    .filter((variety) => !!variety) as Variety[];

  const batchSearchEntities = useSelector(clientSeedBatchesEntitiesSelector);
  const batchSearchIds = useSelector(clientSeedBatchesIdsSelector);
  const isLoadingBatches = useSelector(isLoadingClientSeedBatchesSelector);
  const batchSearchResult = batchSearchIds.length > 0
    ? batchSearchIds
      .map((batchId) => batchSearchEntities[batchId])
      .filter((batch) => !!batch) as SeedBatch[]
    : undefined;

  const clientsEntities = useSelector(clientsEntitiesSelector);
  const clientsIds = useSelector(clientsIdsSelector);
  const isLoadingClients = useSelector(isLoadingClientsSelector);
  const clients = clientsIds
    .map((clientId) => clientsEntities[clientId])
    .filter((client) => !!client) as Client[];

  const [varietySearchIndex, setVarietySearchIndex] = useState<number>();
  const [batchSearchIndex, setBatchSearchIndex] = useState<number>();

  const [internalLoading, setInternalLoading] = useState(false);

  const [
    varietySearchTimeout,
    setVarietySearchTimeout,
  ] = useState<number>();
  const onVarietySearch = (search: string, index: number) => {
    if (!search) {
      dispatch(cleanVarietiesAction());
      setVarietySearchIndex(undefined);
    } else {
      setInternalLoading(true);
      setVarietySearchIndex(index);
      window.clearTimeout(varietySearchTimeout);
      const searchTimeout = window.setTimeout(() => {
        dispatch(getVarietyListAction({
          search,
          isPaginated: false,
        }));
        setInternalLoading(false);
      }, 500);
      setVarietySearchTimeout(searchTimeout);
    }
  };

  const [
    clientSearchTimeout,
    setClientSearchTimeout,
  ] = useState<number>();
  const onClientSearch = (search: string) => {
    if (!search) {
      dispatch(cleanClientsAction());
    } else {
      setInternalLoading(true);
      window.clearTimeout(clientSearchTimeout);
      const searchTimeout = window.setTimeout(() => {
        dispatch(getClientsListAction({
          search,
          isPaginated: false,
        }));
        setInternalLoading(false);
      }, 500);
      setClientSearchTimeout(searchTimeout);
    }
  };

  useEffect(() => {
    const clearEffects = () => {
      window.clearTimeout(varietySearchTimeout);
      window.clearTimeout(clientSearchTimeout);
    };
    return clearEffects;
  }, []);

  const [selectedVarieties, setSelectedVarieties] = useState<(Variety | undefined)[]>([]);
  const [selectedBatches, setSelectedBatches] = useState<SeedBatch[][]>([]);
  const [selectedClient, setSelectedClient] = useState<Client>();

  const selectedVarietyId = batchSearchIndex !== undefined
    ? selectedVarieties[batchSearchIndex]?.id
    : undefined;

  const selectedClientId = selectedClient?.id;

  useEffect(() => {
    dispatch(cleanClientSeedBatchesAction());
    if (selectedVarietyId && selectedClientId) {
      dispatch(getClientSeedBatchesListAction({
        varietyId: selectedVarietyId,
        clientId: selectedClientId,
      }));
    }
  }, [selectedVarietyId, selectedClientId]);

  useEffect(() => {
    if (batchSearchResult && batchSearchIndex !== undefined) {
      const updatedSelectedBatches = [
        ...selectedBatches.slice(0, batchSearchIndex),
        [...batchSearchResult],
        ...selectedBatches.slice(batchSearchIndex + 1),
      ];
      setSelectedBatches(updatedSelectedBatches);
      dispatch(cleanClientSeedBatchesAction());
      setBatchSearchIndex(undefined);
    }
  }, [JSON.stringify(batchSearchResult)]); // For now, this is ok. But be careful.

  const internalOnCancel = () => {
    setSelectedVarieties([]);
    dispatch(cleanVarietiesAction());
    onCancel();
  };

  const onEntryItemAdded = (
    formAdd: (defaultValue?: any, index?: number) => void, // eslint-disable-line
    index: number,
  ) => {
    const updatedSelectedVarieties = [
      ...selectedVarieties.slice(0, index),
      undefined,
      ...selectedVarieties.slice(index + 1),
    ];
    setSelectedVarieties(updatedSelectedVarieties);
    const updatedSelectedBatches = [
      ...selectedBatches.slice(0, index),
      [],
      ...selectedBatches.slice(index + 1),
    ];
    setSelectedBatches(updatedSelectedBatches);
    formAdd({
      varietyId: undefined,
    }, index);
  };

  const onEntryItemRemoved = (formRemove: (index: number | number[]) => void, index: number) => {
    const updatedSelectedVarieties = [
      ...selectedVarieties.slice(0, index),
      ...selectedVarieties.slice(index + 1),
    ];
    setSelectedVarieties(updatedSelectedVarieties);

    const updatedSelectedBatches = [
      ...selectedBatches.slice(0, index),
      ...selectedBatches.slice(index + 1),
    ];
    setSelectedBatches(updatedSelectedBatches);
    formRemove(index);
  };

  const onVarietySelect = (variety: Variety | undefined, index: number) => {
    if (variety) {
      const updatedSelectedVarieties = [
        ...selectedVarieties.slice(0, index),
        { ...variety },
        ...selectedVarieties.slice(index + 1),
      ];
      setSelectedVarieties(updatedSelectedVarieties);
      dispatch(cleanVarietiesAction());
      const { items: formItems } = formHandler.getFieldsValue();
      const updatedItems = [
        ...formItems.slice(0, index),
        {
          ...formItems[index],
          batchId: undefined,
        },
        ...formItems.slice(index + 1),
      ];
      formHandler.setFieldsValue({
        items: updatedItems,
      });
      const updatedSelectedBatches = [
        ...selectedBatches.slice(0, index),
        [],
        ...selectedBatches.slice(index + 1),
      ];
      setSelectedBatches(updatedSelectedBatches);
      setVarietySearchIndex(undefined);
      setBatchSearchIndex(index);
    }
  };

  return (
    <Modal
      visible={isVisible}
      title="Nova saída"
      destroyOnClose
      onCancel={internalOnCancel}
      afterClose={internalOnCancel}
      zIndex={90}
      width={1200}
      footer={[
        <InlineButton
          key="cancel"
          onClick={internalOnCancel}
          type="ghost"
        >
          Cancelar
        </InlineButton>,
        <InlineButton
          key="submit"
          onClick={onSubmit}
          disabled={isLoading}
          loading={isLoading}
        >
          Salvar
        </InlineButton>,
      ]}
    >
      <FormContainer
        layout="vertical"
        form={formHandler}
        requiredMark={false}
        onFinish={onSubmit as (val: unknown) => void}
      >
        <BaseFormItem
          label="Data de entrada"
          name="entryDate"
          initialValue={dayjs()}
          rules={[
            {
              required: true,
              message: 'Insira uma data de entrada',
            },
          ]}
        >
          <DatePicker disabled={isLoading} />
        </BaseFormItem>
        <BaseFormItem
          label="Cliente"
          name="client"
          rules={[
            {
              required: true,
              message: 'Nenhum cliente selecionado',
            },
          ]}
        >
          <Select
            placeholder="Selecione um cliente"
            disabled={isLoading}
            showArrow={false}
            showSearch
            onSearch={onClientSearch}
            onSelect={(clientId) => setSelectedClient(clientsEntities[clientId])}
            filterOption={false}
            notFoundContent={<></>}
            dropdownRender={(menu) => (
              <div>
                <RemoteSelectContent>
                  {menu}
                  {(internalLoading || isLoadingClients) && (
                    <RemoteSelectSpinnerContainer>
                      <Spinner />
                    </RemoteSelectSpinnerContainer>
                  )}
                </RemoteSelectContent>
              </div>
            )}
          >
            {clients.map((clientOption) => (
              <Select.Option
                value={clientOption.id}
                key={clientOption.id}
              >
                {clientOption.name}
              </Select.Option>
            ))}
          </Select>
        </BaseFormItem>
        <FormItemRow>
          <BaseFormItem
            label="Motivo"
            name="outputType"
            rules={[
              {
                required: true,
                message: 'Selecione um motivo',
              },
            ]}
            initialValue={InventoryOutputTypeEnum.PRODUCTION}
          >
            <Select
              disabled={isLoading}
            >
              {Object.values(InventoryOutputTypeEnum).map((reason) => (
                <Select.Option value={reason} key={reason}>
                  {InventoryOutputReasonMapping[reason].name}
                </Select.Option>
              ))}
            </Select>
          </BaseFormItem>
          <BaseFormItem
            label="Centro de custo"
            name="costCenter"
            rules={[
              {
                required: true,
                message: 'Selecione um centro de custo',
              },
            ]}
            initialValue={CostCenterEnum.SEEDING}
          >
            <Select
              disabled={isLoading}
            >
              {Object.values(CostCenterEnum).map((costCenter) => (
                <Select.Option value={costCenter} key={costCenter}>
                  {InventoryCostCenterMapping[costCenter].name}
                </Select.Option>
              ))}
            </Select>
          </BaseFormItem>
        </FormItemRow>
        <h3>Itens</h3>
        <Form.List
          name="items"
          rules={[{
            message: 'Insira pelo menos um item',
            validator: () => {
              const formItems: EntryItemFormData[] = formHandler.getFieldValue('items');
              if (formItems.length > 0) {
                return Promise.resolve();
              }

              return Promise.reject();
            },
          }]}
        >
          {(fields, { add, remove }) => (
            <>
              {fields.map((itemField, index) => (
                <EntryItemFormRow
                  key={itemField.key}
                >
                  <EntryItemInput
                    label="Item"
                    style={{
                      flex: '2',
                    }}
                    name={[itemField.name, 'variety']}
                    validateTrigger="onSubmit"
                    rules={[
                      {
                        required: true,
                        message: 'Selecione um item',
                      },
                    ]}
                  >
                    <Select
                      placeholder="Selecione um item"
                      disabled={isLoading}
                      showArrow={false}
                      showSearch
                      onSearch={(searchValue) => onVarietySearch(searchValue, index)}
                      filterOption={false}
                      onSelect={(
                        varietyId,
                      ) => onVarietySelect(varietySearchEntities[varietyId], index)}
                      notFoundContent={<></>}
                      dropdownRender={(menu) => (
                        <div>
                          <RemoteSelectContent>
                            {menu}
                            {(internalLoading || isLoadingVarieties) && (
                              <RemoteSelectSpinnerContainer>
                                <Spinner />
                              </RemoteSelectSpinnerContainer>
                            )}
                          </RemoteSelectContent>
                        </div>
                      )}
                    >
                      {varietySearchIndex === index
                        ? varietySearchResult.map((varietyOption) => (
                          <Select.Option
                            value={varietyOption.id}
                            key={varietyOption.id}
                          >
                            {`${varietyOption.culture.name} ${varietyOption.name}`}
                          </Select.Option>
                        ))
                        : (() => {
                          const varietyOption = selectedVarieties[index];
                          return varietyOption ? (
                            <Select.Option
                              value={varietyOption.id}
                              key={varietyOption.id}
                            >
                              {`${varietyOption.culture.name} ${varietyOption.name}`}
                            </Select.Option>
                          ) : null;
                        })()}
                    </Select>
                  </EntryItemInput>
                  <BaseFormItem
                    label="Lote"
                    name={[itemField.name, 'batchId']}
                    rules={[
                      {
                        required: true,
                        message: 'Nenhum lote selecionado',
                      },
                    ]}
                  >
                    <Select
                      placeholder="Selecione um lote"
                      disabled={
                        isLoading
                        || !selectedVarieties[index]
                        || (isLoadingBatches && batchSearchIndex === index)
                      }
                      filterOption={false}
                      notFoundContent="Nenhum lote disponível"
                      dropdownRender={(menu) => (
                        <div>
                          <RemoteSelectContent>
                            {menu}
                            {(internalLoading) && (
                              <RemoteSelectSpinnerContainer>
                                <Spinner />
                              </RemoteSelectSpinnerContainer>
                            )}
                          </RemoteSelectContent>
                        </div>
                      )}
                    >
                      {selectedBatches[index]
                        ? selectedBatches[index]?.map((batch) => (
                          <Select.Option
                            value={batch.id}
                            key={batch.id}
                          >
                            {`${batch.batch}
                            (${batch.quantity} sem.)
                            | ${(new Date(batch.expiryDate)).toLocaleDateString('pt-BR')}`}
                          </Select.Option>
                        ))
                        : []}
                    </Select>
                  </BaseFormItem>
                  <QuantityFormItem
                    label="Quantidade"
                    name={[itemField.name, 'quantity']}
                    rules={[
                      {
                        required: true,
                        message: 'Insira uma quantidade',
                      },
                    ]}
                  >
                    <Input
                      type="number"
                      disabled={isLoading || !selectedVarieties[index]}
                      suffix={selectedVarieties[index] ? 'Sem.' : ''}
                    />
                  </QuantityFormItem>
                  <RemoveEntryItemButton onClick={() => onEntryItemRemoved(remove, index)}>
                    <RemoveEntryItemIcon />
                  </RemoveEntryItemButton>
                </EntryItemFormRow>
              ))}
              <AddEntryItemButton
                type="dashed"
                onClick={() => onEntryItemAdded(add, fields.length)}
                icon={<PlusOutlined size={14} />}
              >
                Adicionar item
              </AddEntryItemButton>
            </>
          )}
        </Form.List>
        <NotesInline
          label="Observações"
          name="notes"
        >
          <Input.TextArea disabled={isLoading} rows={5} />
        </NotesInline>
      </FormContainer>
    </Modal>
  );
};

export default EntryOutputModal;
